(() => {
  if (customElements.get('moretti-card-cart')) return;

  const isCartState = (cart) =>
    Boolean(
      cart &&
        Array.isArray(cart.items) &&
        Number.isFinite(Number(cart.item_count)) &&
        Number.isFinite(Number(cart.total_price))
    );

  const parseJsonResponse = async (response) => {
    const text = await response.text();
    let data;

    try {
      data = JSON.parse(text);
    } catch (error) {
      throw new Error('Risposta Shopify non valida. Riprova.');
    }

    if (!response.ok || data?.status || data?.errors) {
      const message =
        data?.description ||
        data?.message ||
        (typeof data?.errors === 'string' ? data.errors : '') ||
        window.cartStrings?.error ||
        'Non è stato possibile aggiornare il carrello.';
      throw new Error(message);
    }

    return data;
  };

  const getSectionRequest = () => {
    const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');

    if (mainCartItems?.dataset.id) sectionIds.push(mainCartItems.dataset.id);
    if (mainCartFooter?.dataset.id) sectionIds.push(mainCartFooter.dataset.id);

    return [...new Set(sectionIds.filter(Boolean))];
  };

  const parseSection = (html) => {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html');
  };

  const renderSections = (cart) => {
    const sections = cart?.sections;
    if (!sections) return [];

    const renderedSections = [];
    const cartDrawer = document.querySelector('cart-drawer');
    const drawerTarget = cartDrawer?.querySelector('#CartDrawer');
    const drawerSource = parseSection(sections['cart-drawer'])?.querySelector('#CartDrawer');

    if (cartDrawer && drawerTarget && drawerSource) {
      const wasOpen = cartDrawer.classList.contains('active');
      drawerTarget.innerHTML = drawerSource.innerHTML;
      cartDrawer.classList.toggle('is-empty', Number(cart.item_count) === 0);
      cartDrawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => cartDrawer.close());
      if (wasOpen) cartDrawer.classList.add('animate', 'active');
      renderedSections.push('cart-drawer');
    }

    const cartBubble = document.getElementById('cart-icon-bubble');
    const bubbleSource = parseSection(sections['cart-icon-bubble'])?.querySelector('.shopify-section');

    if (cartBubble && bubbleSource) {
      cartBubble.innerHTML = bubbleSource.innerHTML;
      renderedSections.push('cart-icon-bubble');
    }

    [
      { id: 'main-cart-items', selector: '.js-contents' },
      { id: 'main-cart-footer', selector: '.js-contents' },
    ].forEach(({ id, selector }) => {
      const targetRoot = document.getElementById(id);
      const sectionId = targetRoot?.dataset.id;
      const target = targetRoot?.querySelector(selector);
      const source = parseSection(sectionId ? sections[sectionId] : '')?.querySelector(selector);

      if (target && source) {
        target.innerHTML = source.innerHTML;
        renderedSections.push(sectionId);
      }
    });

    return [...new Set(renderedSections.filter(Boolean))];
  };

  const STORE = (window.morettiCardCartStore = window.morettiCardCartStore || {
    // An empty cart rendered by Liquid is already authoritative for this page.
    // Mutations and recovery paths still fetch the current Shopify cart.
    cart: isCartState(window.morettiInitialCart) ? window.morettiInitialCart : null,
    loadPromise: null,
    mutationQueue: Promise.resolve(),
    generation: 0,
    appliedGeneration: 0,
    refreshToken: 0,

    enqueue(task) {
      const run = async () => {
        const generation = ++this.generation;
        return task(generation);
      };
      const operation = this.mutationQueue.catch(() => undefined).then(run);
      this.mutationQueue = operation.catch(() => undefined);
      return operation;
    },

    apply(cart, options = {}, generation = this.generation) {
      if (!isCartState(cart)) return false;
      if (generation < this.generation || generation < this.appliedGeneration) return false;

      this.appliedGeneration = generation;
      this.cart = cart;
      document.querySelectorAll('moretti-card-cart').forEach((control) => {
        control.syncFromCart(cart, options);
      });
      return true;
    },

    async fetchAuthoritativeState(generation = this.generation) {
      const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
      const parameters = new URLSearchParams();

      parameters.set('_moretti_generation', String(generation));

      const response = await fetch(`${cartUrl}?${parameters.toString()}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      const cart = await parseJsonResponse(response);

      if (!isCartState(cart)) throw new Error('Stato del carrello Shopify non valido.');
      return cart;
    },

    async fetchRenderedSections(sectionIds, generation = this.generation) {
      const sectionUrl = new URL(window.location.href);
      sectionUrl.searchParams.set('sections', sectionIds.join(','));
      sectionUrl.searchParams.set('_moretti_generation', String(generation));

      const response = await fetch(sectionUrl.toString(), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      return parseJsonResponse(response);
    },

    async synchronize(options = {}, generation = this.generation, providedSections = null) {
      const sectionIds = getSectionRequest();
      const cart = await this.fetchAuthoritativeState(generation);
      const missingSection = sectionIds.some(
        (sectionId) => typeof providedSections?.[sectionId] !== 'string'
      );
      const sections = missingSection
        ? await this.fetchRenderedSections(sectionIds, generation)
        : providedSections;
      const renderedSections = renderSections({ ...cart, sections });
      this.apply(cart, options, generation);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: options.source || 'moretti-card-cart',
          productVariantId: options.preferredVariantId || null,
          variantId: options.preferredVariantId || null,
          cartData: cart,
          sectionsRendered: sectionIds.every((sectionId) => renderedSections.includes(sectionId)),
          renderedSections,
        });
      }

      return cart;
    },

    refresh(options = {}, force = false) {
      const generation = this.generation;
      let requestToken = this.refreshToken;

      if (force || !this.loadPromise) {
        requestToken = ++this.refreshToken;
        const request = this.fetchAuthoritativeState(generation).finally(() => {
          if (this.loadPromise === request) this.loadPromise = null;
        });
        this.loadPromise = request;
      }

      return this.loadPromise.then((cart) => {
        if (requestToken === this.refreshToken) this.apply(cart, options, generation);
        return cart;
      });
    },
  });

  class MorettiCardCart extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.busy = false;
      this.confirmationTimer = null;
      this.explicitVariantSelection = false;
    }

    connectedCallback() {
      // Recommendation cards are re-ordered after every cart update. Moving an
      // ancestor disconnects and reconnects this custom element, so the click
      // listener must be restored even when the control was already initialized.
      this.removeEventListener('click', this.handleClick);
      this.addEventListener('click', this.handleClick);

      if (this.initialized) {
        if (STORE.cart) this.syncFromCart(STORE.cart);
        else this.render();
        return;
      }
      this.initialized = true;

      this.productId = Number(this.dataset.productId);
      this.productTitle = this.dataset.productTitle || '';
      this.defaultVariantId = Number(this.dataset.defaultVariantId);
      this.activeVariantId = Number(this.dataset.activeVariantId) || this.defaultVariantId;
      this.quantity = Math.max(0, Number(this.dataset.initialQuantity) || 0);
      this.compact = this.dataset.compact === 'true';
      this.totalQuantity = Math.max(0, Number(this.dataset.initialTotal) || 0);
      this.countElement = this.querySelector('[data-moretti-card-count]');
      this.requiresVariantSelection = this.dataset.requiresVariantSelection === 'true';
      this.hasQuantityRules = this.dataset.hasQuantityRules === 'true';
      this.productAvailable = this.dataset.productAvailable === 'true';
      this.addButton = this.querySelector('[data-moretti-card-add]');
      this.quantityControl = this.querySelector('[data-moretti-card-quantity-control]');
      this.quantityValue = this.querySelector('[data-moretti-card-quantity]');
      this.minusButton = this.querySelector('[data-moretti-card-minus]');
      this.plusButton = this.querySelector('[data-moretti-card-plus]');
      this.statusElement = this.querySelector('[data-moretti-card-status]');
      this.errorElement = this.querySelector('[data-moretti-card-error]');
      this.variantMap = new Map(
        Array.from(this.querySelectorAll('[data-moretti-card-variant]')).map((element) => {
          const variantId = Number(element.dataset.variantId);
          return [
            variantId,
            {
              id: variantId,
              available: element.dataset.available === 'true',
              min: Math.max(1, Number(element.dataset.min) || 1),
              max: Number(element.dataset.max) || null,
              step: Math.max(1, Number(element.dataset.step) || 1),
            },
          ];
        })
      );

      this.render();

      if (STORE.cart) {
        this.syncFromCart(STORE.cart);
      } else {
        STORE.refresh().catch(() => undefined);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      clearTimeout(this.confirmationTimer);
    }

    handleClick(event) {
      const directAdd = event.target.closest('[data-moretti-card-add][data-moretti-card-mode="direct"]');
      const minus = event.target.closest('[data-moretti-card-minus]');
      const plus = event.target.closest('[data-moretti-card-plus]');

      if (directAdd && this.contains(directAdd)) {
        event.preventDefault();
        this.addInitial();
      } else if (minus && this.contains(minus)) {
        event.preventDefault();
        this.decrement();
      } else if (plus && this.contains(plus)) {
        event.preventDefault();
        this.increment();
      }
    }

    getActiveVariant() {
      return this.variantMap.get(Number(this.activeVariantId));
    }

    getVariantQuantity(cart, variantId) {
      return (cart?.items || []).reduce((total, item) => {
        const itemVariantId = Number(item.variant_id || item.id);
        return itemVariantId === Number(variantId) ? total + (Number(item.quantity) || 0) : total;
      }, 0);
    }

    getVariantsInCart(cart) {
      return Array.from(this.variantMap.keys())
        .map((variantId) => ({
          variantId,
          quantity: this.getVariantQuantity(cart, variantId),
        }))
        .filter(({ quantity }) => quantity > 0);
    }

    syncFromCart(cart, options = {}) {
      if (!isCartState(cart)) return;

      const preferredVariantId = Number(options.preferredVariantId);
      const variantsInCart = this.getVariantsInCart(cart);
      this.totalQuantity = variantsInCart.reduce((total, item) => total + item.quantity, 0);

      if (preferredVariantId && this.variantMap.has(preferredVariantId)) {
        this.activeVariantId = preferredVariantId;
        this.explicitVariantSelection = true;
      }

      if (this.requiresVariantSelection) {
        const activeQuantity = this.getVariantQuantity(cart, this.activeVariantId);

        if (this.explicitVariantSelection && activeQuantity > 0) {
          this.quantity = activeQuantity;
        } else if (variantsInCart.length === 1) {
          this.activeVariantId = variantsInCart[0].variantId;
          this.quantity = variantsInCart[0].quantity;
          this.explicitVariantSelection = false;
        } else if (variantsInCart.length > 1) {
          this.activeVariantId = 0;
          this.quantity = 0;
          this.explicitVariantSelection = false;
        } else {
          this.activeVariantId = this.defaultVariantId;
          this.quantity = 0;
          this.explicitVariantSelection = false;
        }
      } else {
        this.activeVariantId = this.defaultVariantId;
        this.quantity = this.getVariantQuantity(cart, this.activeVariantId);
      }

      const shouldConfirm =
        options.confirm &&
        preferredVariantId &&
        preferredVariantId === Number(this.activeVariantId) &&
        this.quantity > 0;

      this.render();
      if (shouldConfirm) this.showConfirmation();
    }

    render() {
      const variant = this.getActiveVariant();
      const hasQuantity = this.quantity > 0 && Boolean(variant);
      const maximumReached = Boolean(variant?.max && this.quantity >= variant.max);

      if (this.addButton) {
        this.addButton.hidden = !this.compact && hasQuantity;
        if (this.compact) {
          const action = !this.productAvailable ? 'Esaurito' : this.requiresVariantSelection ? 'Scegli formato' : 'Aggiungi al carrello';
          const count = this.totalQuantity > 0 ? `, già nel carrello: ${this.totalQuantity}` : '';
          this.addButton.setAttribute('aria-label', `${action}: ${this.productTitle}${count}`);
        }
        this.addButton.disabled = this.busy || !this.productAvailable;
        this.addButton.setAttribute('aria-disabled', String(this.busy || !this.productAvailable));
      }

      if (this.quantityControl) this.quantityControl.hidden = this.compact || !hasQuantity;
      if (this.countElement) {
        this.countElement.textContent = this.totalQuantity > 99 ? '99+' : String(this.totalQuantity);
        this.countElement.hidden = !this.compact || this.totalQuantity === 0;
      }
      if (this.quantityValue) this.quantityValue.textContent = String(this.quantity);
      if (this.minusButton) this.minusButton.disabled = this.busy || !hasQuantity;
      if (this.plusButton) {
        this.plusButton.disabled = this.busy || !hasQuantity || maximumReached || !variant?.available;
      }
    }

    setBusy(isBusy) {
      this.busy = isBusy;
      this.toggleAttribute('aria-busy', isBusy);

      if (this.addButton?.dataset.morettiCardMode === 'direct') {
        this.addButton.classList.toggle('loading', isBusy);
        this.addButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !isBusy);
      }

      this.render();
    }

    clearError() {
      if (!this.errorElement) return;
      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }

    showError(error) {
      if (!this.errorElement) return;
      this.errorElement.textContent =
        error?.message || window.cartStrings?.error || 'Non è stato possibile aggiornare il carrello.';
      this.errorElement.hidden = false;
    }

    showConfirmation() {
      if (!this.addButton || !this.quantityControl) return;

      clearTimeout(this.confirmationTimer);
      const cartIcon = this.addButton.querySelector('.moretti-card-cart__cart-icon');
      const checkIcon = this.addButton.querySelector('.moretti-card-cart__check-icon');

      this.quantityControl.hidden = true;
      this.addButton.hidden = false;
      if (cartIcon) cartIcon.hidden = true;
      if (checkIcon) checkIcon.hidden = false;
      if (this.statusElement) this.statusElement.textContent = `${this.productTitle} aggiunto al carrello`;

      this.confirmationTimer = setTimeout(() => {
        if (cartIcon) cartIcon.hidden = false;
        if (checkIcon) checkIcon.hidden = true;
        if (this.statusElement) this.statusElement.textContent = '';
        this.render();
      }, 700);
    }

    async runMutation(mutation, options = {}) {
      if (this.busy) return;

      this.clearError();
      this.setBusy(true);

      try {
        await STORE.enqueue(async (generation) => {
          const mutationResult = await mutation();
          await STORE.synchronize(
            {
              preferredVariantId: options.preferredVariantId,
              confirm: options.confirm,
              source: 'moretti-card-cart',
            },
            generation,
            mutationResult?.sections
          );
        });
      } catch (error) {
        this.showError(error);
        await STORE.enqueue((generation) =>
          STORE.synchronize(
            {
              preferredVariantId: options.preferredVariantId,
              source: 'moretti-card-cart-recovery',
            },
            generation
          )
        ).catch(() => undefined);
      } finally {
        this.setBusy(false);
      }
    }

    async addInitial() {
      const variant = this.getActiveVariant();
      if (this.busy || !variant?.available) return;
      const amount = this.quantity > 0 ? variant.step : Math.max(1, variant.min);
      if (variant.max && this.quantity + amount > variant.max) {
        this.showError(new Error(`Quantità massima raggiunta: ${variant.max}.`));
        return;
      }
      await this.addQuantity(amount, true);
    }

    async increment() {
      const variant = this.getActiveVariant();
      if (this.busy || !variant?.available || this.quantity <= 0) return;

      if (variant.max && this.quantity + variant.step > variant.max) {
        this.showError(new Error(`Quantità massima raggiunta: ${variant.max}.`));
        this.render();
        return;
      }

      await this.addQuantity(variant.step, false);
    }

    async addQuantity(amount, confirm) {
      const variant = this.getActiveVariant();
      if (!variant || amount <= 0) return;

      await this.runMutation(
        async () => {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{ id: variant.id, quantity: amount }],
              sections: getSectionRequest(),
              sections_url: `${window.location.pathname}${window.location.search}`,
            }),
          });
          return parseJsonResponse(response);
        },
        {
          preferredVariantId: variant.id,
          confirm,
        }
      );
    }

    async decrement() {
      const variant = this.getActiveVariant();
      if (this.busy || !variant || this.quantity <= 0) return;

      await this.runMutation(
        async () => {
          const cart = STORE.cart || (await STORE.fetchAuthoritativeState());
          const matchingItems = (cart.items || []).filter(
            (item) => Number(item.variant_id || item.id) === variant.id
          );
          const amountToRemove = this.quantity <= variant.min ? this.quantity : variant.step;
          let remaining = amountToRemove;
          const updates = {};

          [...matchingItems].reverse().forEach((item) => {
            if (remaining <= 0) return;
            const itemQuantity = Number(item.quantity) || 0;
            const removeFromLine = Math.min(itemQuantity, remaining);
            updates[item.key] = itemQuantity - removeFromLine;
            remaining -= removeFromLine;
          });

          if (!Object.keys(updates).length) throw new Error('Prodotto non trovato nel carrello.');

          const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              updates,
              sections: getSectionRequest(),
              sections_url: `${window.location.pathname}${window.location.search}`,
            }),
          });
          return parseJsonResponse(response);
        },
        {
          preferredVariantId: variant.id,
          confirm: false,
        }
      );
    }
  }

  customElements.define('moretti-card-cart', MorettiCardCart);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event = {}) => {
      if (event.source === 'moretti-card-cart' || event.source === 'moretti-card-cart-recovery') {
        return Promise.resolve();
      }

      const cartData = event.cartData;
      const preferredVariantId =
        Number(event.productVariantId || event.variantId || cartData?.variant_id || cartData?.id) || null;
      const confirm = event.source === 'product-form' && Boolean(preferredVariantId);

      if (isCartState(cartData)) {
        STORE.apply(cartData, { preferredVariantId, confirm }, STORE.generation);
        return Promise.resolve();
      }

      return STORE.refresh({ preferredVariantId, confirm }, true).catch(() => undefined);
    });
  }
})();
